'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { Skin } from '@/ui/skin/Skin'
import { cva } from 'class-variance-authority'
import { useContext } from 'react'
import { Radio, RadioGroupStateContext } from 'react-aria-components'
import { RadioBullet } from '../radio/RadioBullet.client'
import type { RadioCardProps } from './RadioCard.types'

type RadioCardState = 'default' | 'active' | 'error' | 'disabled'

// Hover and Focus are not resolvable in JS, they are read off the Radio, which is the direct child
// carrying the interaction state, with :has() since the card is its ancestor
const radioCardCva = cva(
  twMerge(
    'relative flex flex-col gap-2 rounded-sm border-px border-solid p-2 hd:border-1.5',
    'outline outline-2 outline-offset-2 outline-clear has-[>[data-focus-visible]]:outline-brand-1-800',
  ),
  {
    variants: {
      state: {
        // Default and Disabled have no border color of their own, it comes from the compound
        // variants below, only the hover override lives here
        default: 'has-[>[data-hovered]]:border-brand-1-900',
        active: 'border-brand-2-900',
        error: 'border-error-fg',
        disabled: '',
      } satisfies Record<RadioCardState, string>,

      // Active and Error are left out, they already carry a border that carries meaning
      withBorder: {
        true: '',
        false: '',
      },
    },
    // Exactly one border color is emitted, so the hover override above is never competing with a
    // second border-color on the same element and stays the winning rule
    compoundVariants: [
      { state: ['default', 'disabled'], withBorder: false, class: 'border-clear' },
      { state: ['default', 'disabled'], withBorder: true, class: 'border-brand-2-200' },
    ],
    defaultVariants: {
      state: 'default',
      withBorder: false,
    },
  },
)

const radioCardRowCva = cva('group/radio-card flex w-full items-center gap-2 text-left text-md', {
  variants: {
    state: {
      default: 'cursor-pointer',
      active: 'cursor-pointer',
      error: 'cursor-pointer',
      disabled: 'cursor-default text-brand-2-400',
    } satisfies Record<RadioCardState, string>,
  },
  defaultVariants: {
    state: 'default',
  },
})

const radioCardTitleCva = cva('text-md font-bold leading-light', {
  variants: {
    state: {
      default: 'group-data-[hovered]/radio-card:text-primary',
      active: '',
      error: 'text-error-fg',
      disabled: '',
    } satisfies Record<RadioCardState, string>,
  },
  defaultVariants: {
    state: 'default',
  },
})

function resolveRadioCardState({
  isDisabled,
  isInvalid,
  isSelected,
}: {
  isDisabled: boolean
  isInvalid: boolean
  isSelected: boolean
}): RadioCardState {
  if (isDisabled) return 'disabled'

  if (isInvalid) return 'error'

  if (isSelected) return 'active'

  return 'default'
}

export function RadioCard({
  value,
  title,
  subTitle,
  titleOutlet,
  rightOutlet,
  withImage,
  withBorder = false,
  children,
  loadingOutlet,
  isDisabled: isDisabledProp = false,
  tint = 'body',
  className,
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId = 'radio-card',
}: RadioCardProps) {
  const groupState = useContext(RadioGroupStateContext)
  const isSelected = groupState?.selectedValue === value
  const isInvalid = !!groupState?.isInvalid
  const isGroupDisabled = !!groupState?.isDisabled
  const isDisabled = isDisabledProp || isGroupDisabled

  // Wave has no read only state, so a group that cannot be changed reads as Disabled
  // It keeps the behavior of react-aria though: still focusable and announced, unlike a disabled card
  const isMuted = isDisabled || !!groupState?.isReadOnly
  const state = resolveRadioCardState({ isDisabled: isMuted, isInvalid, isSelected })

  return (
    <Skin
      tint={tint}
      data-testid={dataTestId}
      className={twMerge(
        radioCardCva({ state, withBorder }),
        isDisabled && 'pointer-events-none',
        className,
      )}
    >
      {/* Radio forwards the id to the native input it renders, which is where an external
          label or an aria-describedby has to point, rather than on the card wrapper */}
      <Radio
        id={id}
        value={value}
        isDisabled={isDisabled}
        aria-label={ariaLabel}
        data-testid={`${dataTestId}/control`}
        className={radioCardRowCva({ state })}
      >
        <RadioBullet
          isSelected={isSelected}
          isInvalid={isInvalid}
          isDisabled={isMuted}
          data-testid={`${dataTestId}/chip`}
        />

        <span className="flex flex-1 flex-col gap-0.5">
          <span data-testid={`${dataTestId}/title`} className={radioCardTitleCva({ state })}>
            {title}
          </span>

          {subTitle && (
            <span data-testid={`${dataTestId}/subtitle`} className="font-medium leading-light">
              {subTitle}
            </span>
          )}
        </span>

        {withImage ? (
          <span
            data-testid={`${dataTestId}/images`}
            className="flex flex-1 items-center justify-end gap-2 self-stretch"
          >
            {withImage}
          </span>
        ) : (
          rightOutlet
        )}
      </Radio>

      {titleOutlet}

      {/* The content slot is kept while the group resolves, so the card does not collapse and reflow */}
      {isSelected && children && (
        <div data-testid={`${dataTestId}/content`}>
          {isGroupDisabled
            ? (loadingOutlet ?? <BaseSkeleton className="min-h-[12rem] w-full" />)
            : children}
        </div>
      )}
    </Skin>
  )
}
